import { POST_CATEGORY_LABELS, type PublicPost } from '@hbac/contract';
import { CalendarDays } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { dropVariants, srcSetUpTo } from '../../lib/image-variants';

const CATEGORY_CLS: Record<string, string> = {
  'tin-tuc': 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300',
  'thong-bao': 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  'su-kien': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
};

/** dd/mm/yyyy from the stored `YYYY-MM-DD HH:MM:SS`, which is already Vietnam time: its date is the day to show. */
export function formatPostDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(value ?? '');
}

/** `priority`: the card is above the fold, so its cover is the page's largest
 *  paint and should not wait for the lazy-loading heuristic. `heading` is the
 *  title's level: h2 on the news page (under its h1), h3 under a section's h2. */
export function PostCard({ post, priority = false, heading: Heading = 'h3' }: { post: PublicPost; priority?: boolean; heading?: 'h2' | 'h3' }) {
  return (
    <Link
      to={`/news/${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:shadow-md active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 dark:border-navy-700 dark:bg-navy-800"
    >
      <div className="aspect-[16/9] overflow-hidden bg-slate-100 dark:bg-navy-900">
        {/* Keyed on the address so a new cover (the editor's preview) starts from loading again. */}
        {post.coverUrl ? <Cover key={post.coverUrl} url={post.coverUrl} priority={priority} /> : <NoCover />}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${
              CATEGORY_CLS[post.category] ?? CATEGORY_CLS['tin-tuc']
            }`}
          >
            {POST_CATEGORY_LABELS[post.category]}
          </span>
          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <CalendarDays size={12} /> {formatPostDate(post.publishedAt)}
          </span>
        </div>

        <Heading className="mt-3 line-clamp-2 text-base font-bold leading-snug text-slate-800 transition-colors group-hover:text-brand-700 dark:text-slate-100 dark:group-hover:text-brand-400">
          {post.title}
        </Heading>
        {post.excerpt && (
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {post.excerpt}
          </p>
        )}
      </div>
    </Link>
  );
}

/**
 * The width a card's cover is shown at: one column, two from sm, three from lg
 * (at most 414 px, the 1280 px grid less its gaps). Rounded up; with only 480 and
 * 800 px copies a closer figure would pick the same one at every width.
 */
const CARD_COVER_SIZES = '(min-width: 1024px) 414px, (min-width: 640px) 50vw, 100vw';

/** The copies a card offers: 800 px covers the widest card (414 px) at 2x. */
const CARD_COVER_WIDTHS = [480, 800] as const;

/**
 * A card's cover. The box keeps its 16:9 size and grey from the first frame, so
 * nothing moves when the picture arrives; it fades in (opacity only, none under
 * reduced motion) instead of appearing line by line. If the copies fail the
 * original is tried, and if that fails too the card shows the club's
 * placeholder, never a broken picture.
 */
function Cover({ url, priority }: { url: string; priority: boolean }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  if (failed) return <NoCover />;
  return (
    <img
      src={url}
      srcSet={srcSetUpTo(url, CARD_COVER_WIDTHS)}
      sizes={CARD_COVER_SIZES}
      // Already in the cache: the load event may have passed before React listened.
      ref={(img) => void (img?.complete && img.naturalWidth && setLoaded(true))}
      onLoad={() => setLoaded(true)}
      // First failure: the copies (transformations off), so the original is tried; then the placeholder.
      onError={(e) => (e.currentTarget.getAttribute('srcset') ? dropVariants(e.currentTarget, url) : setFailed(true))}
      alt=""
      width={640}
      height={360}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      // A priority cover is the page's largest paint: shown as it arrives, no fade to wait for.
      className={`h-full w-full object-cover transition duration-300 group-hover:scale-[1.03] motion-reduce:transition-none ${loaded || priority ? '' : 'opacity-0'}`}
    />
  );
}

/** No cover, or none that loads: a branded placeholder rather than a broken image icon. */
function NoCover() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-navy-800 to-brand-700">
      <img src="/logo/ava-white.webp" alt="" width={48} height={48} loading="lazy" decoding="async" className="h-12 w-12 rounded-full opacity-70" />
    </div>
  );
}
